import { MAX_ZOOM_LEVEL } from '@/constant/constants'
import { MARKER_TYPE } from '@/types/marker'
import { formatDate } from '@/ultils'
import { Cluster, MarkerClusterer } from '@googlemaps/markerclusterer'
import { memo, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { TerraDraw, TerraDrawPolygonMode } from 'terra-draw'
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter'
import { SearchIcon } from '../icons'
import { myStyles } from './modes'
import './styles.css'

export const restrictionDefault: google.maps.MapRestriction = {
  latLngBounds: {
    west: -179.999999999,
    east: 179.999999999,
    north: 85,
    south: -85,
  },
  strictBounds: true,
}

interface GoogleMapProps {
  currentPosition?: boolean
  mapOutput?: (map: google.maps.Map) => void
  onSearchChange?: (data: any) => void
  isSearch?: boolean
  isControlType?: boolean
  mapType?: google.maps.MapTypeId
  onChangeType?: (type: google.maps.MapTypeId) => void
  locations?: any
  hideMarkersRef?: number[]
  hideWeatherMarkersRef?: number[]
  sites?: any
  isDrawing?: boolean
  onDrawComplete?: (geoJson: any, polygon: google.maps.Polygon) => void
}

interface HTMLButtonElement {
  type: 'button' | 'submit' | 'reset' | 'buttonNormal' | 'buttonSatellite'
}

const GoogleMap = memo((props: GoogleMapProps) => {
  const {
    mapOutput,
    isSearch,
    onSearchChange,
    isControlType,
    onChangeType,
    mapType,
    currentPosition = true,
    locations,
    hideMarkersRef,
    hideWeatherMarkersRef,
    sites,
    isDrawing,
    onDrawComplete,
  } = props
  const navigate = useNavigate()

  function createNormalControl(map: any) {
    const controlButton = document.createElement(
      'button',
    ) as HTMLButtonElement & HTMLElement
    // Set CSS for the control.
    const isActive =
      google.maps.MapTypeId.ROADMAP ===
      (mapType || google.maps.MapTypeId.ROADMAP)

    controlButton.style.backgroundColor = isActive ? '#004744' : 'transparent'
    controlButton.style.borderRadius = '6px'
    controlButton.style.border = 'none'
    controlButton.style.color = isActive ? '#FFFFFF' : '#5C5C5C'
    controlButton.style.cursor = 'pointer'
    controlButton.style.fontSize = '11.1885px'
    controlButton.style.marginLeft = '4px'
    controlButton.style.fontWeight = '600px'
    controlButton.style.lineHeight = '14px'
    controlButton.style.padding = '6px 28px'
    controlButton.style.textAlign = 'center'
    controlButton.textContent = 'Normal'
    controlButton.type = 'buttonNormal'

    // Setup the click event listeners: simply set the map to Chicago.
    controlButton.addEventListener('click', () => {
      map.setMapTypeId(google.maps.MapTypeId.ROADMAP)
      const buttonButtonSatellite: any = document.querySelector(
        'button[type=buttonSatellite]',
      )
      if (buttonButtonSatellite) {
        buttonButtonSatellite.style.backgroundColor = 'transparent'
        buttonButtonSatellite.style.color = '#5C5C5C'
        controlButton.style.backgroundColor = '#004744'
        controlButton.style.color = '#FFFFFF'
      }
      if (onChangeType) onChangeType(google.maps.MapTypeId.ROADMAP)
    })

    return controlButton
  }

  function createSatelliteControl(map: any) {
    const controlButton = document.createElement(
      'button',
    ) as HTMLButtonElement & HTMLElement
    const isActive =
      google.maps.MapTypeId.HYBRID ===
      (mapType || google.maps.MapTypeId.ROADMAP)
    // Set CSS for the control.
    controlButton.style.backgroundColor = isActive ? '#004744' : 'transparent'
    controlButton.style.borderRadius = '6px'
    controlButton.style.border = 'none'
    controlButton.style.color = isActive ? '#FFFFFF' : '#5C5C5C'
    controlButton.style.cursor = 'pointer'
    controlButton.style.zIndex = '1'
    controlButton.style.fontSize = '11.1885px'
    controlButton.style.fontWeight = '600px'
    controlButton.style.marginLeft = '6px'
    controlButton.style.lineHeight = '14px'
    controlButton.style.padding = '6px 28px'
    controlButton.style.textAlign = 'center'
    controlButton.textContent = 'Satellite'
    controlButton.type = 'buttonSatellite'

    // Setup the click event listeners: simply set the map to Chicago.
    controlButton.addEventListener('click', () => {
      map.setMapTypeId(google.maps.MapTypeId.HYBRID)
      const buttonButtonNormal: any = document.querySelector(
        'button[type=buttonNormal]',
      )
      if (buttonButtonNormal) {
        buttonButtonNormal.style.backgroundColor = 'transparent'
        buttonButtonNormal.style.color = '#5C5C5C'

        controlButton.style.backgroundColor = '#004744'
        controlButton.style.color = '#FFFFFF'
      }
      if (onChangeType) onChangeType(google.maps.MapTypeId.HYBRID)
    })

    return controlButton
  }

  function createGroupButton(map: any) {
    const controlButton = document.createElement('div')

    // Set CSS for the control.
    controlButton.style.backgroundColor = '#FFFFFF'
    controlButton.style.boxShadow = '0px 0px 30px rgba(132, 132, 132, 0.25)'
    controlButton.style.borderRadius = '6px'
    controlButton.style.padding = '4px'
    controlButton.style.height = '35px'
    controlButton.style.width = '207.4px'
    controlButton.style.marginBottom = '15px'
    controlButton.style.marginLeft = '10px'
    controlButton.style.display = 'inline-flex'

    return controlButton
  }

  function createGroupSearch(map: any) {
    const controlButton = document.createElement('div')

    // Set CSS for the control.
    controlButton.style.backgroundColor = 'transparent'

    return controlButton
  }

  function SearchIconMap(map: any) {
    const controlButton = document.getElementById(
      'search-icon',
    ) as HTMLInputElement

    // Set CSS for the control.
    controlButton.style.marginLeft = '257px'
    controlButton.style.marginTop = '24px'
    controlButton.style.position = 'absolute'

    return controlButton
  }

  useEffect(() => {
    initGoogleMap()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stationsDraw = useRef<google.maps.Marker[]>([])
  const stationsOverlayDraw = useRef<google.maps.Marker[]>([])

  class Popup extends google.maps.OverlayView {
    position: google.maps.LatLng
    containerDiv: HTMLDivElement

    constructor(position: google.maps.LatLng, content: HTMLElement) {
      super()
      this.position = position

      content.classList.add('popup-bubble')

      // This zero-height div is positioned at the bottom of the bubble.
      const bubbleAnchor = document.createElement('div')

      bubbleAnchor.classList.add('popup-bubble-anchor')
      bubbleAnchor.appendChild(content)

      // This zero-height div is positioned at the bottom of the tip.
      this.containerDiv = document.createElement('div')
      this.containerDiv.classList.add('popup-container')
      this.containerDiv.appendChild(bubbleAnchor)

      // Optionally stop clicks, etc., from bubbling up to the map.
      Popup.preventMapHitsAndGesturesFrom(this.containerDiv)
    }
    /** Called when the popup is added to the map. */
    onAdd() {
      this.getPanes()!.floatPane.appendChild(this.containerDiv)
    }

    /** Called when the popup is removed from the map. */
    onAddClass() {
      this.containerDiv.classList.add('active')
    }
    onRemoveClass() {
      this.containerDiv.classList.remove('active')
    }

    onRemove() {
      if (this.containerDiv.parentElement) {
        this.containerDiv.parentElement.removeChild(this.containerDiv)
      }
    }

    /** Called each frame when the popup needs to draw itself. */
    draw() {
      const divPosition = this.getProjection().fromLatLngToDivPixel(
        this.position,
      )!
      // Hide the popup when it is far out of view.
      const display =
        Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000
          ? 'block'
          : 'none'

      if (display === 'block') {
        this.containerDiv.style.left = divPosition.x + 'px'
        this.containerDiv.style.top = divPosition.y + 'px'
      }

      if (this.containerDiv.style.display !== display) {
        this.containerDiv.style.display = display
      }
    }
  }

  const [mapGoogle, setMapGoogle] = useState<google.maps.Map | null>(null)
  const [popups, setPopups] = useState<Popup[]>([])

  const initGoogleMap = () => {
    if (!window.google) {
      setTimeout(() => {
        initGoogleMap()
      }, 1000)
      return
    }
    const refMap = document.getElementById('GoogleMap')

    if (!refMap) return

    const mapProperties: google.maps.MapOptions = {
      center: new google.maps.LatLng(1.2271, 103.8431),
      zoom: 18,
      mapTypeId: mapType || google.maps.MapTypeId.HYBRID,
      disableDefaultUI: true,
      tilt: 0,
      mapTypeControlOptions: {
        mapTypeIds: ['roadmap', 'satellite', 'hybrid', 'terrain'],
      },
      restriction: {
        latLngBounds: {
          west: -179.999999999,
          east: 179.999999999,
          north: 85,
          south: -85,
        },
        strictBounds: true,
      },
      backgroundColor: 'hsla(0, 0%, 0%, 0)',
      styles: myStyles,
    }

    const map = new google.maps.Map(refMap, mapProperties)
    setMapGoogle(map)

    let infoWindow = new google.maps.InfoWindow({
      content: '',
      disableAutoPan: true,
    })

    ///Zeus
    locations &&
      locations.map((position: any, i: any) => {
        const position2 = { lat: position.latitude, lng: position.longitude }
        let iconUrl = position.weatherStationIcon.icon
          ? position.weatherStationIcon.icon.url
          : ''
        const markerOptions = {
          position: position2,
          id: position.weatherStationIcon?.icon?.id,
          weatherLinkStationId: position.weatherLinkStationId,
          powerSupply: position.powerSupply,
          icon: {
            url: iconUrl,
            scaledSize: new google.maps.Size(28, 28),
            labelOrigin: new google.maps.Point(15, 15),
          },
          map: map,
          zIndex: 1,
        }
        const marker = new google.maps.Marker(markerOptions)
        marker.set('customZeus', markerOptions)
        stationsDraw.current.push(marker)

        google.maps.event.addListener(infoWindow, 'domready', function () {
          const el = document.querySelector(`.popup-hover-box`) as HTMLElement
          const parent =
            el?.parentNode?.parentNode?.parentNode?.parentNode?.parentNode
              ?.firstElementChild
          if (parent && !parent.classList.contains('custom-infor-popup')) {
            parent.classList.add('custom-infor-popup')
          }
        })
        const contentString = `
        <div id="content" class="popup-hover-box">
          <div class="stylePopups">
            <h3 class="title">${position.name}</h3>
            <div class="box" id="myList">
            </div>
            <div class="text-inline" id="myDate">
            </div>
            <div class="battery" id="js-battery"></div>
          </div>
          <div class="styleFooter">
            <button id="js-chart" class="btn btn1">btn1</button>
            <button id="js-time" class="btn btn2">btn2</button>
          </div>
        </div>`

        const contentWeatherLinkString = `
        <div id="content" class="popup-hover-box">
          <div class="stylePopups">
           <button id="js-weatherLink" class='btnWeather'>Weather Station Dashboard</button>
          </div>
        </div>`

        infoWindow.setContent(contentString)
        infoWindow.open(map, marker)
        infoWindow.close()
        marker.addListener('click', () => {
          var object = position.data
          if (object && position.sourceName === 'WeatherLink') {
            infoWindow.setContent(contentWeatherLinkString)
            infoWindow.open(map, marker)
            var button3 = document.getElementById(
              'js-weatherLink',
            ) as HTMLElement
            button3.addEventListener('click', function () {
              localStorage.setItem(
                'idWeatherStations',
                position.weatherLinkStationId,
              )
              navigate('/dashboard/sensors')
            })
          }
          if (object && position.sourceName === 'Zeus') {
            infoWindow.setContent(contentString)
            infoWindow.open(map, marker)
            const keyNew: any = []
            const objectNew: any = []
            for (var key in object) {
              keyNew.push(key)
              objectNew.push(object[key])
              const listItem = `<div class="box-center"><p class="box_left">${key}</p><p class="box_right">${object[key]}</p></div>`
              let newHtml = document.getElementById(
                'myList',
              ) as HTMLInputElement
              newHtml.insertAdjacentHTML('beforeend', listItem)
              let htmlString = `<p>Last Updated:</p><p>${formatDate(
                position.lastTimeData,
              )}</p>`
              let newDate = document.getElementById(
                'myDate',
              ) as HTMLInputElement
              newDate.innerHTML = htmlString
            }
            let htmlBattery
            if (position.powerSupply === 'green') {
              htmlBattery = '<p class="iconFull">Good condition</p>'
            } else if (position.powerSupply === 'orange') {
              htmlBattery =
                '<p class="iconWorking">Must replace batteries in 3 months</p>'
            } else if (position.powerSupply === 'red') {
              htmlBattery =
                '<p class="iconHalf">Must replace batteries within current month</p>'
            } else {
              htmlBattery = ''
            }
            let newBattery = document.getElementById(
              'js-battery',
            ) as HTMLInputElement
            newBattery.innerHTML = htmlBattery
          }
          var button = document.getElementById('js-chart') as HTMLElement
          button.addEventListener('click', function () {
            localStorage.setItem('idZeus', position.weatherLinkStationId)
            localStorage.setItem('synoptic', 'false')
            navigate('/dashboard/chart-zeus')
          })

          var button1 = document.getElementById('js-time') as HTMLElement
          button1.addEventListener('click', function () {
            localStorage.setItem('synoptic', 'true')
            navigate('/dashboard/chart-zeus')
          })
        })

        return marker
      })

    if (isControlType) {
      createControlType(map)
    }

    if (isSearch) {
      const searchIconControl = SearchIconMap(map)
      const groupSearchControl = createGroupSearch(map)
      const input = document.getElementById('pac-input') as HTMLInputElement
      groupSearchControl.appendChild(input)
      groupSearchControl.appendChild(searchIconControl)

      map.controls[google.maps.ControlPosition.TOP_LEFT].push(
        groupSearchControl,
      )
      const searchBox = new google.maps.places.SearchBox(input)

      searchBox.addListener('places_changed', () => {
        const places = searchBox.getPlaces()
        if (!places || !places[0]) {
          return
        }
        if (onSearchChange) onSearchChange(places[0])
      })
    }

    if (navigator.geolocation && currentPosition) {
      navigator.geolocation.getCurrentPosition((position) => {
        const pos = new google.maps.LatLng(
          position.coords.latitude,
          position.coords.longitude,
        )
        map.setCenter(pos)
      })
    }

    if (mapOutput) mapOutput(map)
  }

  const drawMarker = () => {
    stationsDraw.current.forEach((marker, index) => {
      // Check if the marker is customZeus
      const state: any = marker.get('customZeus')

      if (
        hideMarkersRef &&
        (hideMarkersRef.includes(state?.id) ||
          hideMarkersRef?.includes(state?.weatherLinkStationId) ||
          hideWeatherMarkersRef?.includes(state?.weatherLinkStationId))
      ) {
        marker.setVisible(false)
      } else {
        marker.setVisible(true)
      }
    })
  }

  useEffect(() => {
    if (sites) {
      //Overlay
      stationsOverlayDraw.current.forEach((marker) => {
        marker.setMap(null)
      })
      stationsOverlayDraw.current = []

      if (!mapGoogle) return
      sites &&
        sites.forEach((position: any, i: any) => {
          const position2 = {
            lat: position.overlay.bottomLeftLatitude,
            lng: position.overlay.bottomLeftLongitude,
          }
          let iconUrl =
            position.overlay.iconId === MARKER_TYPE.SRC_Controller
              ? '/assets/map_marker_c.svg'
              : position.overlay.imageUrl || ''
          const markerOptions = {
            position: position2,
            id: position.overlay.id,
            iconId: position.overlay.iconId,
            icon: {
              url: iconUrl,
            },
            label: {
              text: position.overlay.interfaceName, // Text to display below the icon
              color: '#004744', // Color of the text
              fontSize: '12px', // Font size of the text
              fontWeight: '700',
              className: 'custom-marker-label',
            },
            map: mapGoogle,
            zIndex: 2,
          }
          const marker = new google.maps.Marker(markerOptions)
          marker.set('overlaySite', markerOptions)
          stationsOverlayDraw.current.push(marker)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites, mapGoogle])

  const drawOverlay = () => {
    stationsOverlayDraw.current.forEach((marker, index) => {
      // Check if the marker is customZeus
      const state: any = marker.get('overlaySite')
      if (hideMarkersRef && hideMarkersRef?.includes(state?.iconId)) {
        marker.setVisible(false)
      } else {
        marker.setVisible(true)
      }
    })
  }

  const zoomLevel = mapGoogle && mapGoogle.getZoom()

  let markerCluster: MarkerClusterer | any = null

  const updatePopup = () => {
    if (!mapGoogle) return
    if (!locations) {
      popups.forEach((popup) => popup.setMap(null))
      setPopups([])
      return
    }

    const renderer = {
      render({ markers, count, position }: Cluster) {
        if (!markers || markers.length === 0) {
          // Return a default invisible marker instead of null
          return new google.maps.Marker({
            position: position,
            visible: false,
          })
        }

        const hasGreen = markers.some(
          (marker) => (marker as any).get('customZeus').powerSupply === 'green',
        )
        const hasRed = markers.some(
          (marker) => (marker as any).get('customZeus').powerSupply === 'red',
        )

        const weatherStationGreenPointUrl =
          locations && locations[0].weatherStationIcon.weatherStationGreenPoint
        const weatherStationRedPointUrl =
          locations && locations[0].weatherStationIcon.weatherStationRedPoint

        let iconUrl
        if (hasRed && hasGreen) {
          iconUrl = weatherStationRedPointUrl
        } else if (hasRed) {
          iconUrl = weatherStationRedPointUrl
        } else if (hasGreen) {
          iconUrl = weatherStationGreenPointUrl
        } else {
          iconUrl = ''
        }

        return new google.maps.Marker({
          position,
          icon: {
            url: iconUrl,
            scaledSize: new google.maps.Size(75, 75),
          },
          label: {
            text: String(count),
            color: 'rgba(255,255,255,0.9)',
            fontSize: '12px',
          },
          // adjust zIndex to be above other markers
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        })
      },
    }

    if (zoomLevel && zoomLevel > 12) {
      popups.forEach((popup) => popup.setMap(null))
      setPopups([])
      if (markerCluster) {
        markerCluster.clearMarkers()
        markerCluster = null
      }

      const filteredLocations =
        locations &&
        locations.filter((location: any) => {
          const weatherStationId = location.weatherLinkStationId
          const iconId = location.weatherStationIcon?.icon?.id

          const hideMarkers = hideMarkersRef ?? [] // Default to empty array if undefined
          const hideWeatherMarkers = hideWeatherMarkersRef ?? [] // Default to empty array if undefined

          const isHiddenByMarkers =
            hideMarkers.includes(iconId) ||
            hideMarkers.includes(weatherStationId)
          const isHiddenByWeather =
            hideWeatherMarkers.includes(iconId) ||
            hideWeatherMarkers.includes(weatherStationId)

          // Only include locations that are not hidden by either ref
          return (
            !(isHiddenByMarkers || isHiddenByWeather) &&
            zoomLevel &&
            zoomLevel > 16
          )
        })

      // Create new popups for the filtered locations
      const newPopups: Popup[] =
        filteredLocations?.map((position: any) => {
          const position2 = {
            lat: position.latitude,
            lng: position.longitude,
          }

          const content = document.createElement('div')
          content.textContent = position.name

          const popup = new Popup(
            new google.maps.LatLng(position2.lat, position2.lng),
            content,
          )
          popup.setMap(mapGoogle)
          return popup
        }) || []

      setPopups(newPopups)
    } else {
      markerCluster = new MarkerClusterer({
        map: mapGoogle,
        markers: stationsDraw.current || [],
        renderer: renderer,
        algorithmOptions: {
          maxZoom: MAX_ZOOM_LEVEL,
        },
      })

      popups.forEach((popup) => popup.setMap(null))
      setPopups([])
    }
  }

  useEffect(() => {
    if (locations) {
      drawMarker()
      updatePopup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideWeatherMarkersRef, hideMarkersRef, locations, zoomLevel])

  useEffect(() => {
    if (sites) {
      drawOverlay()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideMarkersRef, sites, zoomLevel])

  useEffect(() => {
    if (!mapGoogle || !isDrawing) return

    let terraDrawActive = true

    const draw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({
        lib: google.maps,
        map: mapGoogle,
      }),
      modes: [
        new TerraDrawPolygonMode({
          styles: {
            fillColor: '#66ff99',
            fillOpacity: 0.5,
            outlineColor: '#e60000',
            outlineWidth: 3,
          },
        }),
      ],
    })

    draw.start()
    draw.setMode('polygon')

    const handleFinish = (id: string | number) => {
      const feature = draw.getSnapshotFeature(id)
      if (!feature || feature.geometry.type !== 'Polygon') return

      const geoJson = { geometry: feature.geometry }
      const ring = (feature.geometry as GeoJSON.Polygon).coordinates[0]
      const path = ring.slice(0, -1).map(([lng, lat]) => ({ lat, lng }))

      const nativePolygon = new google.maps.Polygon({
        paths: path,
        fillColor: '#66ff99',
        fillOpacity: 0.5,
        strokeColor: '#e60000',
        strokeWeight: 3,
        editable: true,
        map: mapGoogle,
      })

      const accepted = onDrawComplete?.(geoJson, nativePolygon)

      if (!accepted) {
        nativePolygon.setMap(null)
        draw.clear()
        draw.setMode('polygon')
      } else {
        draw.clear()
        terraDrawActive = false
        draw.stop()
      }
    }

    draw.on('finish', handleFinish)

    return () => {
      if (terraDrawActive) {
        draw.stop()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapGoogle, isDrawing])

  const createControlType = (map: google.maps.Map) => {
    const groupButtonControl = createGroupButton(map)
    const normalControl = createNormalControl(map)
    const satelliteControl = createSatelliteControl(map)

    groupButtonControl.appendChild(normalControl)
    groupButtonControl.appendChild(satelliteControl)
    map.controls[google.maps.ControlPosition.BOTTOM_LEFT].push(
      groupButtonControl,
    )
  }

  return (
    <>
      <div
        id="GoogleMap"
        style={{ width: '100%', height: '100%', flexGrow: 1 }}
      />
      {isSearch && (
        <div>
          <div id="search-icon">
            <SearchIcon />
          </div>

          <input
            id="pac-input"
            className="inputSearch icon"
            type="text"
            placeholder="Search Place"
          />
        </div>
      )}
    </>
  )
})

export default GoogleMap
